/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchManyToManyFunc,
	makeJoinEqualsFunc,
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsGenreEntity from 'Models/Security/Acl/VisitorsGenreEntity';
import AgencyAdminGenreEntity from 'Models/Security/Acl/AgencyAdminGenreEntity';
import ApplicationUserGenreEntity from 'Models/Security/Acl/ApplicationUserGenreEntity';
import AgencyPlaylisterGenreEntity from 'Models/Security/Acl/AgencyPlaylisterGenreEntity';
import MustardManagementGenreEntity from 'Models/Security/Acl/MustardManagementGenreEntity';
import MustardAdminGenreEntity from 'Models/Security/Acl/MustardAdminGenreEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IGenreEntityAttributes extends IModelAttributes {
	name: string;

	trackss: Array<
		| Models.TracksGenres
		| Models.ITracksGenresAttributes
	>;
	primaryGenres: Array<
		| Models.SecondaryGenrePrimaryGenre
		| Models.ISecondaryGenrePrimaryGenreAttributes
	>;
	secondaryGenres: Array<
		| Models.SecondaryGenrePrimaryGenre
		| Models.ISecondaryGenrePrimaryGenreAttributes
	>;
	trackMetadatas: Array<
		| Models.TrackMetadataEntity
		| Models.ITrackMetadataEntityAttributes
	>;
	// % protected region % [Add any custom attributes to the interface here] on begin
	primaryGenreList: string;
	secondaryGenreList: string;
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('GenreEntity', 'Genre')
// % protected region % [Customise your entity metadata here] end
export default class GenreEntity extends Model
	implements IGenreEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsGenreEntity(),
		new AgencyAdminGenreEntity(),
		new ApplicationUserGenreEntity(),
		new AgencyPlaylisterGenreEntity(),
		new MustardManagementGenreEntity(),
		new MustardAdminGenreEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Name'] off begin
	@Validators.Unique()
	@observable
	@attribute<GenreEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;
	// % protected region % [Modify props to the crud options here for attribute 'Name'] end

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Tracks'] off begin
		name: 'Tracks',
		displayType: 'reference-multicombobox',
		order: 20,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.TracksGenres,
		optionEqualFunc: makeJoinEqualsFunc('tracksId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'genreEntity',
			oppositeEntityName: 'trackEntity',
			relationName: 'genres',
			relationOppositeName: 'tracks',
			entity: () => Models.GenreEntity,
			joinEntity: () => Models.TracksGenres,
			oppositeEntity: () => Models.TrackEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Tracks'] end
	})
	public trackss: Models.TracksGenres[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Primary Genre'] off begin
		name: 'Primary Genre',
		displayType: 'reference-multicombobox',
		order: 30,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.SecondaryGenrePrimaryGenre,
		optionEqualFunc: makeJoinEqualsFunc('primaryGenreId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'genreEntity',
			oppositeEntityName: 'genreEntity',
			relationName: 'secondaryGenre',
			relationOppositeName: 'primaryGenre',
			entity: () => Models.GenreEntity,
			joinEntity: () => Models.SecondaryGenrePrimaryGenre,
			oppositeEntity: () => Models.GenreEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Primary Genre'] end
	})
	public primaryGenres: Models.SecondaryGenrePrimaryGenre[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Secondary Genre'] off begin
		name: 'Secondary Genre',
		displayType: 'reference-multicombobox',
		order: 40,
		isJoinEntity: true,
		referenceTypeFunc: () => Models.SecondaryGenrePrimaryGenre,
		optionEqualFunc: makeJoinEqualsFunc('secondaryGenreId'),
		referenceResolveFunction: makeFetchManyToManyFunc({
			entityName: 'genreEntity',
			oppositeEntityName: 'genreEntity',
			relationName: 'primaryGenre',
			relationOppositeName: 'secondaryGenre',
			entity: () => Models.GenreEntity,
			joinEntity: () => Models.SecondaryGenrePrimaryGenre,
			oppositeEntity: () => Models.GenreEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Secondary Genre'] end
	})
	public secondaryGenres: Models.SecondaryGenrePrimaryGenre[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Track Metadata'] off begin
		name: 'Track Metadatas',
		displayType: 'reference-multicombobox',
		order: 50,
		referenceTypeFunc: () => Models.TrackMetadataEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'trackMetadatas',
			oppositeEntity: () => Models.TrackMetadataEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Track Metadata'] end
	})
	public trackMetadatas: Models.TrackMetadataEntity[] = [];

	// % protected region % [Add any custom attributes to the model here] on begin
	@CRUD({
		name: 'Primary Genres',
		isReadonly: true,
		displayType: 'hidden',
		order: 99,
		headerColumn: true,
		searchable: false,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public primaryGenreList: string;

	@CRUD({
		name: 'Secondary Genres',
		isReadonly: true,
		displayType: 'hidden',
		order: 100,
		headerColumn: true,
		searchable: false,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public secondaryGenreList: string;
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IGenreEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] on begin
		// This is assigned in assignAttributes but somehow
		// it doesn't show in CRUD tables calling the following
		// this.models = data[dataModelName].map((e: any) => new ModelType(e));
		// Putting this here assigns it correctly
		if (attributes?.primaryGenreList) {
			this.primaryGenreList = attributes.primaryGenreList;
		}
		if (attributes?.secondaryGenreList) {
			this.secondaryGenreList = attributes.secondaryGenreList;
		}
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IGenreEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.trackss !== undefined && Array.isArray(attributes.trackss)) {
				for (const model of attributes.trackss) {
					if (model instanceof Models.TracksGenres) {
						this.trackss.push(model);
					} else {
						this.trackss.push(new Models.TracksGenres(model));
					}
				}
			}
			if (attributes.primaryGenres !== undefined && Array.isArray(attributes.primaryGenres)) {
				for (const model of attributes.primaryGenres) {
					if (model instanceof Models.SecondaryGenrePrimaryGenre) {
						this.primaryGenres.push(model);
					} else {
						this.primaryGenres.push(new Models.SecondaryGenrePrimaryGenre(model));
					}
				}
			}
			if (attributes.secondaryGenres !== undefined && Array.isArray(attributes.secondaryGenres)) {
				for (const model of attributes.secondaryGenres) {
					if (model instanceof Models.SecondaryGenrePrimaryGenre) {
						this.secondaryGenres.push(model);
					} else {
						this.secondaryGenres.push(new Models.SecondaryGenrePrimaryGenre(model));
					}
				}
			}
			if (attributes.trackMetadatas !== undefined && Array.isArray(attributes.trackMetadatas)) {
				for (const model of attributes.trackMetadatas) {
					if (model instanceof Models.TrackMetadataEntity) {
						this.trackMetadatas.push(model);
					} else {
						this.trackMetadatas.push(new Models.TrackMetadataEntity(model));
					}
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		primaryGenres {
			${Models.SecondaryGenrePrimaryGenre.getAttributes().join('\n')}
			primaryGenre {
				${Models.GenreEntity.getAttributes().join('\n')}
			}
		}
		trackss {
			${Models.TracksGenres.getAttributes().join('\n')}
			tracks {
				${Models.TrackEntity.getAttributes().join('\n')}
				${Models.TrackEntity.getFiles().map(f => f.name).join('\n')}
			}
		}
		secondaryGenres {
			${Models.SecondaryGenrePrimaryGenre.getAttributes().join('\n')}
			secondaryGenre {
				${Models.GenreEntity.getAttributes().join('\n')}
			}
		}
		trackMetadatas {
			${Models.TrackMetadataEntity.getAttributes().join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			primaryGenres: {},
			trackss: {},
			secondaryGenres: {},
			trackMetadatas: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'trackss',
							'primaryGenres',
							'secondaryGenres',
						],
					},
				],
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return this.name;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] on begin
	public listExpands = `
		primaryGenreList
		secondaryGenreList
	`;
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(GenreEntity.prototype, 'created');
CRUD(modifiedAttr)(GenreEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
